import { Box, Button, Grid, Step, StepLabel, Stepper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { useCallback, useRef, useState } from 'react';
import { useMultiStepForm } from 'src/shared/hooks/useMultiStepForm';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { MultiStepIcon } from 'src/shared/components/multistepper/multiStepIcon';
import { StyledMultiStepConnector } from 'src/shared/components/multistepper/styledMultiStepConnector';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { useGlobals } from 'src/shared/contexts/appContext';
import { createMemberInvoices } from 'src/shared/services/invoiceService';
import { useInvoiceContext } from 'src/shared/contexts/invoiceContext';
import { InvoiceDeliveryMethodIntegerEnum } from 'src/shared/models/enums/invoiceDeliveryMethodInteger.enum';
import { PermissionEnum } from 'src/shared/models/enums/permissionEnums';
import { getPermission } from 'src/shared/functions/getSysProductPermissions';
import { useNavigate } from 'react-router-dom';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { InvoiceSendingTypeIntegerEnum } from 'src/shared/models/enums/invoiceSendingTypeInteger.enum';
import { CreateMemberInvoice } from './createMemberInvoice';
import { MemberInvoiceSelectionComponent } from './memberInvoiceSelectionComponent';
import { InvoiceTemplate } from '../models/invoiceTemplate';
import { CreateMemberInvoicesRequest } from '../models/createMemberInvoicesRequest';

type CreateMemberInvoiceStepperModalProps = {
  modalOpen: boolean;
  onClose: () => void;
};

interface CreateInvoiceMission {
  templateId: number;
  defaultText?: string;
  defaultHeading?: string;
  emailHeading?: string;
  emailText?: string;
  payDate: string;
  missionName: string;
  missionDate: string;
  excludeDate: string;
  invoiceDeliveryMethod: InvoiceDeliveryMethodIntegerEnum;
  InvoiceTemplate?: InvoiceTemplate;
  approvalRequired: boolean;
  sndDate?: string;
  runAsTaskWithTaskService: boolean;
}

export const CreateMemberInvoiceStepperModal = (props: CreateMemberInvoiceStepperModalProps) => {
  const { modalOpen, onClose } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const { setDateStamp, currentUser } = useGlobals();
  const { invoiceCountStats, setInvoiceCountStats } = useInvoiceContext();
  const navigate = useNavigate();

  const isSuperAdmin = getPermission(currentUser, PermissionEnum.InvoiceCreateForCustomer);

  const [loading, setLoading] = useState(false);
  const form = useRef(null);
  const [serverErrors] = useState<string[]>([]);
  const modalTop = useRef<HTMLDivElement>(null);

  const redirectToInvoiceMission = useCallback(
    (id) => {
      if (id && Number(id)) navigate(`${RoutesEnum.InvoiceMissionDetail}/${id}/${InvoiceSendingTypeIntegerEnum.NotSet}`);
    },
    [navigate],
  );

  const minimumCharRequired = 5;
  const minCharRequiredMessage = t('common:validationMessages.minLength', { count: minimumCharRequired });
  const MemberInvoiceSelectionSchema: yup.Schema<CreateInvoiceMission> = yup.object().shape({
    templateId: yup.number().required(),
    defaultHeading: yup.string().optional(),
    defaultText: yup.string().optional(),
    emailHeading: yup.string().optional(),
    emailText: yup.string().optional(),
    payDate: yup.string().required(),
    missionName: yup.string().min(minimumCharRequired, minCharRequiredMessage).required(),
    missionDate: yup.string().required(),
    excludeDate: yup.string().required(),
    invoiceDeliveryMethod: yup.number().required(),
    invoiceTemplateId: yup.object().required(),
    approvalRequired: yup.boolean().required(),
    sndDate: yup.string().when('approvalRequired', {
      is: true,
      then: (sndDate) => sndDate.required(t('common:validationMessages.required')),
      otherwise: (sndDate) => sndDate.optional(),
    }),
    runAsTaskWithTaskService: yup.boolean().required(),
  });

  const formMethods = useForm<CreateInvoiceMission>({
    resolver: yupResolver(MemberInvoiceSelectionSchema),
    mode: 'all',
    defaultValues: {
      templateId: undefined,
      defaultHeading: '',
      defaultText: '',
      emailHeading: '',
      emailText: '',
      payDate: DateHelper.getDate(dayjs(new Date()).add(35, 'day')),
      missionName: '',
      missionDate: DateHelper.getDate(new Date()),
      excludeDate: DateHelper.getDate(dayjs(new Date()).subtract(1, 'day')),
      invoiceDeliveryMethod: InvoiceDeliveryMethodIntegerEnum.print,
      approvalRequired: isSuperAdmin,
      sndDate: undefined,
      // Ska alltid köras via TaskService; superadmin kan kryssa ur manuellt, övriga får det automatiskt.
      runAsTaskWithTaskService: true,
    },
  });

  const { handleSubmit, watch, reset, getValues } = formMethods;

  // Steg i formuläret
  const { step, steps, currentStepIndex, isFirstStep, isLastStep, back, next, goTo } = useMultiStepForm([
    <MemberInvoiceSelectionComponent />, //
    <CreateMemberInvoice setLoading={setLoading} tempId={getValues('templateId')} isSuperAdmin={isSuperAdmin} />,
  ]);

  const excludeDateIsInvalid = !watch('excludeDate') || !dayjs(watch('excludeDate')).isValid();
  const stepTwoDateIsInvalid =
    !watch('missionDate') || !watch('payDate') || !dayjs(watch('missionDate')).isValid() || !dayjs(watch('payDate')).isValid();

  // Ordningen ska vara samma som i useMultiStepForm
  const stepOneInvalid = !watch('templateId') || excludeDateIsInvalid;
  const stepTwoInvalid = !watch('templateId') || stepTwoDateIsInvalid || !(watch('missionName').length > 4);
  const isStepComplete = [!stepOneInvalid, !stepTwoInvalid];

  const allStepsComplete = isStepComplete.every((v) => v === null || v);

  const onHandleClose = useCallback(() => {
    setInvoiceCountStats(undefined);
    goTo(0);
    reset();
    onClose();
  }, [goTo, onClose, reset, setInvoiceCountStats]);

  const onSubmit = useCallback(
    async (data: CreateInvoiceMission) => {
      setLoading(true);
      const dataToPost: CreateMemberInvoicesRequest = {
        templateId: data.templateId,
        payDate: DateHelper.getFSDate(data.payDate),
        missionName: data.missionName,
        missionDate: DateHelper.getFSDate(data.missionDate),
        excludeDate: DateHelper.getDate(data.excludeDate),
        invoiceDeliveryMethod: data.invoiceDeliveryMethod,
        async: true,
        approvalRequired: data.approvalRequired,
        sndDate: data.approvalRequired ? data.sndDate : undefined,
        runAsTaskWithTaskService: data.runAsTaskWithTaskService,
      };
      try {
        const res = await createMemberInvoices(dataToPost);
        if (isMounted()) {
          toast.success(<>{t('invoice:createMemberInvoice.createInvoices')}</>);
          if (res) {
            redirectToInvoiceMission(res.data);
          }
          setDateStamp(new Date());
        }
      } catch (e) {
        if (isMounted()) {
          ConsoleHelper.log(e);
          toast.error(<>{t('invoice:createMemberInvoice.createInvoicesFailed')}</>);
        }
      } finally {
        setLoading(false);
        onHandleClose();
      }
    },
    [isMounted, onHandleClose, redirectToInvoiceMission, setDateStamp, t],
  );

  const onHandleSave = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  return (
    <GenericDialog
      open={modalOpen}
      padding={0}
      disableBackdropClosing
      dialogTitle={t('invoice:createMemberInvoice.modalHeader')}
      onClose={onHandleClose}
      onSave={onHandleSave}
      showDialogActions
      dialogActions={
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {!isFirstStep ? (
            <Button
              type='button'
              variant='outlined'
              onClick={() => {
                back();
                setInvoiceCountStats(undefined);
              }}
              disabled={loading}
              startIcon={<ArrowBackIos fontSize='small' />}
            >
              {t('common:button.back')}
            </Button>
          ) : (
            <Box />
          )}
          {!isLastStep && currentStepIndex === 0 && (
            <Button
              type='button'
              variant='contained'
              sx={{ mr: 1 }}
              disabled={excludeDateIsInvalid || !watch('templateId') || !(invoiceCountStats && invoiceCountStats?.totalCount > 0)}
              disableElevation
              onClick={next}
              endIcon={<ArrowForwardIos fontSize='small' />}
            >
              {t('common:button.continue')}
            </Button>
          )}
          {isLastStep && currentStepIndex === 1 && (
            <Button
              type='submit'
              variant='contained'
              sx={{ mr: 1 }}
              disabled={!allStepsComplete || loading}
              disableElevation
              onClick={() => {
                next();
                onHandleSave();
              }}
              endIcon={<ArrowForwardIos fontSize='small' />}
            >
              {t('invoice:createMemberInvoice.stepLabels.step1')}
            </Button>
          )}
        </Box>
      }
      dialogContent={
        <Box
          ref={modalTop}
          sx={{
            position: 'relative',
            border: (theme) => (serverErrors.length > 0 ? `8px solid ${theme.palette.error.main}` : 'none'),
            minHeight: '22rem',
          }}
        >
          <LoadingOverlay loading={loading} />
          <Stepper activeStep={currentStepIndex} alternativeLabel sx={{ mb: 2, mt: 3 }} connector={<StyledMultiStepConnector />}>
            {steps.map((_item, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <Step key={i}>
                <StepLabel StepIconComponent={(stepProps) => <MultiStepIcon {...stepProps} isDisabled isComplete={isStepComplete[i]} />}>
                  {i + 1}. {t(`invoice:createMemberInvoice:stepLabels.step${i}`)}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
          <GridWrapper>
            <Grid item xs={12} mt={1.5}>
              <FormProvider {...formMethods}>
                <form onSubmit={handleSubmit(onSubmit)} ref={form}>
                  {step}
                </form>
              </FormProvider>
            </Grid>
          </GridWrapper>
        </Box>
      }
    />
  );
};
